import * as Sentry from '@sentry/vue';

/**
 * Log an unhandled / unexpected error.
 *
 * Always writes to `console.error`, and forwards to Sentry:
 *   - if any arg is an `Error`, capture it as an exception (with the full arg
 *     list as extra context);
 *   - otherwise capture the first arg stringified as a message at "error" level.
 *
 * Sentry only. This used to capture into PostHog as well, which billed a second
 * copy of every error we already read in Sentry — and only in Sentry, since it
 * is the tool holding this app's releases, source maps and `ignoreErrors`.
 *
 * Sentry is initialized on test and production and skipped on local, so a
 * developer-machine error stays in the console — see [src/main.ts](src/main.ts)
 * and [src/lib/env.ts](src/lib/env.ts).
 *
 * Signature mirrors `console.error`:
 *
 * ```ts
 * try { ... } catch (err) { logError('Failed to load invoice', err); }
 * ```
 */
export function logError(...args: unknown[]): void {
  console.error(...args);

  const err = args.find((a) => a instanceof Error);
  if (err instanceof Error) {
    Sentry.captureException(err, { extra: { args } });
  } else if (args.length > 0) {
    Sentry.captureMessage(String(args[0]), 'error');
  }
}
